import { Calendar, Download, FileJson, FileSpreadsheet, FileText } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../lib/api/client';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useToast } from './ui/toast';

export type ExportFormat = 'csv' | 'json' | 'ics' | 'pdf';

interface ExportOption {
  format: ExportFormat;
  label: string;
  description?: string;
  /** Async — returns when the download has started (or thrown). */
  run: () => Promise<void>;
}

interface ExportMenuProps {
  options: ExportOption[];
  /** Optional override for the trigger label. Defaults to "Export". */
  label?: string;
  size?: 'sm' | 'md';
  variant?: 'outline' | 'primary' | 'ghost';
}

const formatIcon: Record<ExportFormat, React.ComponentType<{ className?: string }>> = {
  csv: FileSpreadsheet,
  json: FileJson,
  ics: Calendar,
  pdf: FileText,
};

export function ExportMenu({
  options,
  label = 'Export',
  size = 'md',
  variant = 'outline',
}: ExportMenuProps): JSX.Element {
  const [busy, setBusy] = useState(false);
  const { showToast } = useToast();

  async function trigger(opt: ExportOption): Promise<void> {
    setBusy(true);
    try {
      await opt.run();
    } catch (err) {
      const description =
        err instanceof ApiError ? err.message : ((err as Error).message ?? 'export failed');
      showToast({
        title: `Could not export ${opt.format.toUpperCase()}`,
        description,
        variant: 'danger',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} disabled={busy}>
          <Download className="h-4 w-4" />
          {busy ? 'Exporting…' : label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]">
        <DropdownMenuLabel>Download as</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((opt) => {
          const Icon = formatIcon[opt.format];
          return (
            <DropdownMenuItem key={`${opt.format}-${opt.label}`} onSelect={() => void trigger(opt)}>
              <Icon className="h-4 w-4 text-ink-muted" />
              <div className="flex flex-col">
                <span>{opt.label}</span>
                {opt.description ? (
                  <span className="text-xs text-ink-subtle">{opt.description}</span>
                ) : null}
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
